/**
 * Installment service with API integration
 */
import { InstallmentType } from '../types/enums';
import { apiService } from './api';
import { requireEmployeeId } from './userContext';

export interface Installment {
  _id?: string;
  id?: string;
  installmentId: string;
  installmentCardId: string;
  paymentdate: string;
  postponeDate?: string;
  installmentFee: number;
  InstallmentState: string;
  installmentType: InstallmentType;
  debt?: number;
  Remark?: string;
  referance?: string;
  tripId?: string;
  billNumber?: string;
  balance?: number;
  routeId?: string;
  assignedCollector?: string;
  isCommissionPaid?: boolean;
  whoAdded: string;
  whoUpdated?: string;
  createdAt: string;
  updatedAt: string;
  status?: string;
}

export interface InstallmentsListResponse {
  success: boolean;
  data: {
    installments: Installment[];
    pagination?: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  };
  message?: string;
}

export const installmentService = {
  /**
   * Get all installments with optional filtering
   */
  async listInstallments(params?: {
    page?: number;
    size?: number;
    searchTerm?: string;
    installmentCardId?: string;
    InstallmentState?: string;
  }): Promise<InstallmentsListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.size) queryParams.append('size', params.size.toString());
      if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params?.installmentCardId) queryParams.append('installmentCardId', params.installmentCardId);
      if (params?.InstallmentState) queryParams.append('InstallmentState', params.InstallmentState);

      const queryString = queryParams.toString();
      const url = `/installments/get-all${queryString ? `?${queryString}` : ''}`;
      
      console.log('[INSTALLMENT-SERVICE] Fetching installments from:', url);
      const response = await apiService.get<any>(url);
      console.log('[INSTALLMENT-SERVICE] Raw response:', response);
      console.log('[INSTALLMENT-SERVICE] Response type:', typeof response);
      console.log('[INSTALLMENT-SERVICE] Response keys:', Object.keys(response || {}));
      
      // Handle nested response structure similar to deliveryPackage service
      // Expected: { success: true, data: { installments: [...], pagination: {...} } }
      let data = response;
      
      // Check if we have the nested structure
      if (response.data && response.data.data) {
        data = response.data.data;
      } else if (response.data) {
        data = response.data;
      }
      
      // Ensure we return the correct structure
      const result: InstallmentsListResponse = {
        success: response.success === true || response.status === 'SUCCESS' || true,
        data: {
          installments: data.installments || [],
          pagination: data.pagination || {
            page: params?.page || 1,
            size: params?.size || 10,
            total: data.installments?.length || 0,
            totalPages: 1
          }
        }
      };
      
      console.log('[INSTALLMENT-SERVICE] Processed result:', result);
      console.log('[INSTALLMENT-SERVICE] Installments count:', result.data.installments.length);
      
      return result;
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error listing installments:', error);
      throw error;
    }
  },

  /**
   * Get installments by installment card ID
   */
  async getInstallmentsByInstallmentCardId(
    installmentCardId: string,
    options?: { size?: number },
  ): Promise<InstallmentsListResponse> {
    try {
      console.log('[INSTALLMENT-SERVICE] Fetching installments for card ID:', installmentCardId);
      const response = await this.listInstallments({
        installmentCardId,
        size: options?.size ?? 1000,
      });
      
      console.log('[INSTALLMENT-SERVICE] Response for card ID:', installmentCardId, response);
      console.log('[INSTALLMENT-SERVICE] Installments array:', response.data?.installments);
      
      // The response should already be in the correct format from listInstallments
      // But ensure we have the installments array
      if (response.success && response.data && Array.isArray(response.data.installments)) {
        console.log('[INSTALLMENT-SERVICE] Successfully extracted', response.data.installments.length, 'installments');
        return response;
      }
      
      // Fallback: if structure is unexpected, try to extract installments
      console.warn('[INSTALLMENT-SERVICE] Unexpected response structure, attempting to extract installments');
      return {
        success: true,
        data: {
          installments: [],
          pagination: {
            page: 1,
            size: 0,
            total: 0,
            totalPages: 1
          }
        }
      };
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error fetching installments by card ID:', error);
      throw error;
    }
  },

  /**
   * Get a single installment by ID
   */
  async getInstallment(installmentId: string) {
    try {
      const response = await apiService.get(`/installments/${installmentId}`);
      return response;
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error fetching installment:', error);
      throw error;
    }
  },

  /**
   * Create a new installment
   */
  async createInstallment(data: Partial<Installment>) {
    const employeeId = requireEmployeeId();
    try {
      const payload = {
        ...data,
        whoAdded: employeeId  // Override any whoAdded in data
      };
      const response = await apiService.post('/installments/create', payload);
      return response;
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error creating installment:', error);
      throw error;
    }
  },

  /**
   * Update an installment (full update)
   */
  async updateInstallment(installmentId: string, data: Partial<Installment>) {
    // Automatically get employeeId from localStorage
    const employeeId = requireEmployeeId();
    try {
      const payload = {
        ...data,
        whoUpdated: employeeId  // Override any whoUpdated in data
      };
      const response = await apiService.put(`/installments/${installmentId}`, payload);
      return response;
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error updating installment:', error);
      throw error;
    }
  },

  /**
   * Partially update an installment
   */
  async partialUpdateInstallment(installmentId: string, data: Partial<Installment>) {
    // Automatically get employeeId from localStorage
    const employeeId = requireEmployeeId();
    try {
      const payload = {
        ...data,
        whoUpdated: employeeId  // Override any whoUpdated in data
      };
      const response = await apiService.patch(`/installments/${installmentId}`, payload);
      return response;
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error partially updating installment:', error);
      throw error;
    }
  },

  /**
   * Delete an installment
   */
  async deleteInstallment(installmentId: string) {
    try {
      const response = await apiService.delete(`/installments/${installmentId}`);
      return response;
    } catch (error) {
      console.error('[INSTALLMENT-SERVICE] Error deleting installment:', error);
      throw error;
    }
  }
};
